/**
 * Shared sportsbook constants (server + frontend). SPORTSBOOK_REGISTRY is canonical:
 * each book has a stable `id` plus per-vendor keys, because the same book differs per
 * provider (Caesars is `williamhill_us` on The Odds API, `caesars` on SharpSports).
 * The source lists below derive from it.
 */

import { getOddsApiRegions, isLeagueKey, type LeagueKey, oddsApiRegionsIncludeUs } from './league';

export interface SportsbookEntry {
  id: string;
  name: string;
  /** Key The Odds API (US) expects; null = not available there. */
  oddsApiKey: string | null;
  /** Key SharpSports expects for account linking; null = not linkable. */
  sharpSportsKey: string | null;
}

export const SPORTSBOOK_REGISTRY = {
  fanduel: { id: 'fanduel', name: 'FanDuel', oddsApiKey: 'fanduel', sharpSportsKey: 'fanduel' },
  draftkings: {
    id: 'draftkings',
    name: 'DraftKings',
    oddsApiKey: 'draftkings',
    sharpSportsKey: 'draftkings',
  },
  betmgm: { id: 'betmgm', name: 'BetMGM', oddsApiKey: 'betmgm', sharpSportsKey: 'betmgm' },
  // Caesars: Odds API key is williamhill_us (paid tier), SharpSports key is caesars.
  caesars: {
    id: 'caesars',
    name: 'Caesars',
    oddsApiKey: 'williamhill_us',
    sharpSportsKey: 'caesars',
  },
  espnbet: { id: 'espnbet', name: 'ESPN BET', oddsApiKey: 'espnbet', sharpSportsKey: null },
  betrivers: {
    id: 'betrivers',
    name: 'BetRivers',
    oddsApiKey: 'betrivers',
    sharpSportsKey: 'betrivers',
  },
  fanatics: {
    id: 'fanatics',
    name: 'Fanatics',
    oddsApiKey: 'fanatics',
    sharpSportsKey: 'fanatics',
  },
  // SharpSports-only (account linking / bet import; no US Odds API feed).
  prizepicks: {
    id: 'prizepicks',
    name: 'PrizePicks',
    oddsApiKey: null,
    sharpSportsKey: 'prizepicks',
  },
  underdog: { id: 'underdog', name: 'Underdog', oddsApiKey: null, sharpSportsKey: 'underdog' },
  sleeper: { id: 'sleeper', name: 'Sleeper', oddsApiKey: null, sharpSportsKey: 'sleeper' },
  hardrock: { id: 'hardrock', name: 'Hard Rock', oddsApiKey: null, sharpSportsKey: 'hardrock' },
  betfred: { id: 'betfred', name: 'BetFred', oddsApiKey: null, sharpSportsKey: 'betfred' },
  fliff: { id: 'fliff', name: 'Fliff', oddsApiKey: null, sharpSportsKey: 'fliff' },
} as const satisfies Record<string, SportsbookEntry>;

export type SportsbookId = keyof typeof SPORTSBOOK_REGISTRY;

/** Map a book id to the key The Odds API expects; falls back to fanduel for unknown/removed ids. */
export function getOddsApiKey(id: string): string {
  const entry = (SPORTSBOOK_REGISTRY as Record<string, SportsbookEntry>)[id];
  return entry?.oddsApiKey ?? SPORTSBOOK_REGISTRY.fanduel.oddsApiKey;
}

/**
 * Reverse of getOddsApiKey: map an Odds API bookmaker key back to our canonical id
 * (e.g. williamhill_us -> caesars). Returns null for a key we do not recognize, so
 * callers can keep the raw key rather than drop the book.
 */
export function getSportsbookIdByOddsApiKey(oddsApiKey: string): string | null {
  for (const entry of Object.values(SPORTSBOOK_REGISTRY) as SportsbookEntry[]) {
    if (entry.oddsApiKey === oddsApiKey) return entry.id;
  }
  return null;
}

// Books available from The Odds API (US), the fetch/source list. sportsbook.test.ts
// asserts this matches every registry entry with a non-null oddsApiKey.
export const SPORTSBOOKS_ODDS_API = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'espnbet',
  'betrivers',
  'fanatics',
] as const;

/** A book id available on The Odds API (the books valid for odds/selection). */
export type OddsApiSportsbookId = (typeof SPORTSBOOKS_ODDS_API)[number];

// EU/UK sportsbooks for European leagues (EuroLeague)
export const EU_ODDS_API_SPORTSBOOKS = [
  'pinnacle',
  'betfair_ex_eu',
  'unibet_eu',
  'williamhill',
  'sport888',
] as const;

export type EuOddsApiSportsbookId = (typeof EU_ODDS_API_SPORTSBOOKS)[number];

export function isEuOddsApiSportsbookId(value: unknown): value is EuOddsApiSportsbookId {
  return EU_ODDS_API_SPORTSBOOKS.some((book) => book === value);
}

export const DEFAULT_EU_SPORTSBOOK = 'pinnacle' as const;

/** The EU books that post EuroLeague moneyline, spreads and totals, so a user may choose one. */
export const EU_GAME_MARKET_SPORTSBOOKS = ['pinnacle', 'sport888', 'williamhill'] as const;

export type EuGameMarketSportsbookId = (typeof EU_GAME_MARKET_SPORTSBOOKS)[number];

export function isEuGameMarketSportsbookId(value: unknown): value is EuGameMarketSportsbookId {
  return EU_GAME_MARKET_SPORTSBOOKS.some((book) => book === value);
}

// Books linkable via SharpSports (account linking / bet import), the source list.
// sportsbook.test.ts asserts this matches every registry entry with a non-null sharpSportsKey.
export const SPORTSBOOKS_SHARPSPORTS = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'betrivers',
  'fanatics',
  'prizepicks',
  'underdog',
  'sleeper',
  'hardrock',
  'betfred',
  'fliff',
] as const;

// All known book ids (every registry entry).
export const ALL_SPORTSBOOKS = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'espnbet',
  'betrivers',
  'fanatics',
  'prizepicks',
  'underdog',
  'sleeper',
  'hardrock',
  'betfred',
  'fliff',
] as const;

// Type for SharpSports sportsbooks (used for account linking)
export type SharpSportsSportsbook = (typeof SPORTSBOOKS_SHARPSPORTS)[number];

// Type for any sportsbook
export type Sportsbook = (typeof ALL_SPORTSBOOKS)[number];

export function isSportsbook(value: unknown): value is Sportsbook {
  return ALL_SPORTSBOOKS.some((book) => book === value);
}

// Default sportsbook (must be an Odds API book, since it is the fallback for fetching/selection)
export const DEFAULT_SPORTSBOOK: OddsApiSportsbookId = 'fanduel';

/** The book system runs price player props at: the daily generation and the slate table's default. */
export const SYSTEM_SPORTSBOOK: OddsApiSportsbookId = 'draftkings';

function isOddsApiSportsbookId(value: unknown): value is OddsApiSportsbookId {
  return SPORTSBOOKS_ODDS_API.some((book) => book === value);
}

/** The Odds API key a US request for `book` (our id or its Odds API key) uses; unknown books get the default. */
function usOddsApiBookFor(book: string): string {
  const lower = book.toLowerCase();
  const id = getSportsbookIdByOddsApiKey(lower) ?? lower;
  return getOddsApiKey(isOddsApiSportsbookId(id) ? id : DEFAULT_SPORTSBOOK);
}

/** The bookmaker key an odds request for `book` in `leagueKey` names; an EU league also takes US books if it asks 'us'. */
export function oddsApiBookFor(book: string, leagueKey: LeagueKey): string {
  if (getOddsApiRegions(leagueKey) === 'us') return usOddsApiBookFor(book);
  const lower = book.toLowerCase();
  if (isEuGameMarketSportsbookId(lower)) return lower;
  const usId = getSportsbookIdByOddsApiKey(lower) ?? lower;
  if (oddsApiRegionsIncludeUs(leagueKey) && isOddsApiSportsbookId(usId)) return getOddsApiKey(usId);
  return DEFAULT_EU_SPORTSBOOK;
}

/** The bookmaker of a game's spread and total where a prediction stores them (the ML's training
 * rows) and where the ML batch scorer and pre-game rescore serve them. */
export function gameLineBookFor(leagueKey: LeagueKey): string {
  return oddsApiBookFor(DEFAULT_SPORTSBOOK, leagueKey);
}

/** A book a user may choose for some league: an Odds API US book or a EuroLeague book. */
export type ChoosableBookId = OddsApiSportsbookId | EuGameMarketSportsbookId;

export function isChoosableBookId(value: unknown): value is ChoosableBookId {
  return isOddsApiSportsbookId(value) || isEuGameMarketSportsbookId(value);
}

/** The books a picker offers for a league. */
export function choosableBooksForLeague(leagueKey: LeagueKey): readonly ChoosableBookId[] {
  return getOddsApiRegions(leagueKey) === 'us' ? SPORTSBOOKS_ODDS_API : EU_GAME_MARKET_SPORTSBOOKS;
}

/** The preference key that holds the user's book for a league. */
export function sportsbookPreferenceKey(
  leagueKey: LeagueKey,
): 'preferredSportsbook' | 'preferredEuSportsbook' {
  return getOddsApiRegions(leagueKey) === 'us' ? 'preferredSportsbook' : 'preferredEuSportsbook';
}

/** The book to send for a league, from raw stored preferences; anything unusable gets the default. */
export function sportsbookForLeague(preferences: unknown, leagueKey: LeagueKey): ChoosableBookId {
  const stored =
    preferences !== null && typeof preferences === 'object'
      ? Reflect.get(preferences, sportsbookPreferenceKey(leagueKey))
      : undefined;
  if (getOddsApiRegions(leagueKey) === 'us') {
    return isOddsApiSportsbookId(stored) ? stored : DEFAULT_SPORTSBOOK;
  }
  return isEuGameMarketSportsbookId(stored) ? stored : DEFAULT_EU_SPORTSBOOK;
}

/** The book id stored for an Odds API key: our id, or the key itself for books we have no id for. */
export function recordedBookFor(oddsApiBook: string): string {
  return getSportsbookIdByOddsApiKey(oddsApiBook) ?? oddsApiBook;
}

/** The bookmaker that answered a request for `book`, for the league in the data's `sport_key`. */
export function findBookmaker<B extends { key: string }>(
  propsData: { sport_key?: string; bookmakers?: readonly B[] },
  book: string,
): B | undefined {
  const sportKey = propsData.sport_key;
  const key =
    sportKey !== undefined && isLeagueKey(sportKey)
      ? oddsApiBookFor(book, sportKey)
      : usOddsApiBookFor(book);
  return propsData.bookmakers?.find((b) => b.key === key);
}

/**
 * Books whose line moves send a user slate move alerts: their chosen list (empty means none),
 * else their chosen sportsbook, else the default. Takes raw stored preferences.
 */
export function getSlateAlertBooks(preferences: unknown): OddsApiSportsbookId[] {
  if (preferences !== null && typeof preferences === 'object') {
    if ('slateAlertBooks' in preferences && Array.isArray(preferences.slateAlertBooks)) {
      return preferences.slateAlertBooks.filter(isOddsApiSportsbookId);
    }
    if (
      'preferredSportsbook' in preferences &&
      isOddsApiSportsbookId(preferences.preferredSportsbook)
    ) {
      return [preferences.preferredSportsbook];
    }
  }
  return [DEFAULT_SPORTSBOOK];
}

/** Short display abbreviation for sportsbooks (e.g., "DK" for DraftKings) */
export const SPORTSBOOK_SHORT_NAMES: Record<string, string> = {
  fanduel: 'FD',
  draftkings: 'DK',
  betmgm: 'MGM',
  caesars: 'CZR',
  espnbet: 'ESPN',
  bovada: 'BOV',
  mybookieag: 'MB',
  betrivers: 'BR',
  prizepicks: 'PP',
  underdog: 'UD',
  fanatics: 'FAN',
  sleeper: 'SLP',
  hardrock: 'HR',
  betfred: 'BFD',
  fliff: 'FLF',
  // EU/UK sportsbooks
  pinnacle: 'PIN',
  betfair_ex_eu: 'BF',
  unibet_eu: 'UNI',
  williamhill: 'WH',
  sport888: '888',
};

/** Get short book abbreviation; accepts our id or an Odds API key (williamhill_us -> CZR). */
export function getShortBookName(book: string): string {
  const id = getSportsbookIdByOddsApiKey(book) ?? book;
  return SPORTSBOOK_SHORT_NAMES[id] || book.slice(0, 3).toUpperCase();
}

// Max legs per parlay by sportsbook
const SPORTSBOOK_MAX_LEGS: Record<string, number> = {
  fanduel: 25,
  draftkings: 12,
  betmgm: 15,
  caesars: 25,
  espnbet: 20,
  betrivers: 12,
  prizepicks: 6,
  underdog: 8,
  fanatics: 25,
  sleeper: 8,
  hardrock: 20,
  betfred: 15,
  fliff: 6,
};

export const DEFAULT_MAX_LEGS = 15;

/** Get the maximum number of legs allowed for a sportsbook */
export function getMaxLegsForSportsbook(sportsbook: string | null): number {
  if (!sportsbook) return DEFAULT_MAX_LEGS;
  return SPORTSBOOK_MAX_LEGS[sportsbook] ?? DEFAULT_MAX_LEGS;
}

/**
 * Whether a sportsbook chip is non-selectable in a picker (web SportsbookSelector parity).
 * Non-Odds-API books are account-linking only; an Odds-API book absent from `coveredBooks` (the
 * canonical-id list from /api/sportsbooks/coverage) is disabled so a user cannot pick a book with
 * no odds. Fail-safe: null/empty coverage disables nothing, and the current selection stays enabled.
 */
export function isSportsbookChipDisabled(
  book: { value: string; oddsApiSupported: boolean },
  coveredBooks: string[] | null,
  selectedSportsbook: string,
): boolean {
  if (!book.oddsApiSupported) return true;
  if (coveredBooks == null || coveredBooks.length === 0) return false;
  if (book.value === selectedSportsbook) return false;
  return !coveredBooks.includes(book.value);
}

// Sportsbook color configurations (for badges/UI)
export interface SportsbookColorConfig {
  id: Sportsbook;
  name: string;
  // Tailwind classes for web
  dot: string;
  bg: string;
  text: string;
  // Hex colors for React Native
  dotHex: string;
  bgHex: string;
  textHex: string;
}

export const SPORTSBOOK_COLORS: Record<string, SportsbookColorConfig> = {
  fanduel: {
    id: 'fanduel',
    name: 'FanDuel',
    dot: 'bg-blue-500',
    bg: 'bg-blue-500/15',
    text: 'text-blue-400',
    dotHex: '#3b82f6',
    bgHex: 'rgba(59, 130, 246, 0.15)',
    textHex: '#60a5fa',
  },
  draftkings: {
    id: 'draftkings',
    name: 'DraftKings',
    dot: 'bg-green-500',
    bg: 'bg-green-500/15',
    text: 'text-green-400',
    dotHex: '#22c55e',
    bgHex: 'rgba(34, 197, 94, 0.15)',
    textHex: '#4ade80',
  },
  betmgm: {
    id: 'betmgm',
    name: 'BetMGM',
    dot: 'bg-amber-500',
    bg: 'bg-amber-500/15',
    text: 'text-amber-400',
    dotHex: '#f59e0b',
    bgHex: 'rgba(245, 158, 11, 0.15)',
    textHex: '#fbbf24',
  },
  caesars: {
    id: 'caesars',
    name: 'Caesars',
    dot: 'bg-purple-500',
    bg: 'bg-purple-500/15',
    text: 'text-purple-400',
    dotHex: '#a855f7',
    bgHex: 'rgba(168, 85, 247, 0.15)',
    textHex: '#c084fc',
  },
  espnbet: {
    id: 'espnbet',
    name: 'ESPN BET',
    dot: 'bg-red-500',
    bg: 'bg-red-500/15',
    text: 'text-red-400',
    dotHex: '#ef4444',
    bgHex: 'rgba(239, 68, 68, 0.15)',
    textHex: '#f87171',
  },
  betrivers: {
    id: 'betrivers',
    name: 'BetRivers',
    dot: 'bg-sky-500',
    bg: 'bg-sky-500/15',
    text: 'text-sky-400',
    dotHex: '#0ea5e9',
    bgHex: 'rgba(14, 165, 233, 0.15)',
    textHex: '#38bdf8',
  },
  prizepicks: {
    id: 'prizepicks',
    name: 'PrizePicks',
    dot: 'bg-purple-500',
    bg: 'bg-purple-500/15',
    text: 'text-purple-400',
    dotHex: '#a855f7',
    bgHex: 'rgba(168, 85, 247, 0.15)',
    textHex: '#c084fc',
  },
  underdog: {
    id: 'underdog',
    name: 'Underdog',
    dot: 'bg-amber-500',
    bg: 'bg-amber-500/15',
    text: 'text-amber-400',
    dotHex: '#f59e0b',
    bgHex: 'rgba(245, 158, 11, 0.15)',
    textHex: '#fbbf24',
  },
  fanatics: {
    id: 'fanatics',
    name: 'Fanatics',
    dot: 'bg-indigo-500',
    bg: 'bg-indigo-500/15',
    text: 'text-indigo-400',
    dotHex: '#6366f1',
    bgHex: 'rgba(99, 102, 241, 0.15)',
    textHex: '#818cf8',
  },
  sleeper: {
    id: 'sleeper',
    name: 'Sleeper',
    dot: 'bg-cyan-500',
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-400',
    dotHex: '#06b6d4',
    bgHex: 'rgba(6, 182, 212, 0.15)',
    textHex: '#22d3ee',
  },
  hardrock: {
    id: 'hardrock',
    name: 'Hard Rock',
    dot: 'bg-orange-500',
    bg: 'bg-orange-500/15',
    text: 'text-orange-400',
    dotHex: '#f97316',
    bgHex: 'rgba(249, 115, 22, 0.15)',
    textHex: '#fb923c',
  },
  betfred: {
    id: 'betfred',
    name: 'BetFred',
    dot: 'bg-rose-500',
    bg: 'bg-rose-500/15',
    text: 'text-rose-400',
    dotHex: '#f43f5e',
    bgHex: 'rgba(244, 63, 94, 0.15)',
    textHex: '#fb7185',
  },
  fliff: {
    id: 'fliff',
    name: 'Fliff',
    dot: 'bg-pink-500',
    bg: 'bg-pink-500/15',
    text: 'text-pink-400',
    dotHex: '#ec4899',
    bgHex: 'rgba(236, 72, 153, 0.15)',
    textHex: '#f472b6',
  },
  // EU/UK sportsbooks
  pinnacle: {
    id: 'fanduel', // placeholder — not in Sportsbook union, display only
    name: 'Pinnacle',
    dot: 'bg-yellow-500',
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-400',
    dotHex: '#eab308',
    bgHex: 'rgba(234, 179, 8, 0.15)',
    textHex: '#facc15',
  },
  betfair_ex_eu: {
    id: 'fanduel',
    name: 'Betfair',
    dot: 'bg-lime-500',
    bg: 'bg-lime-500/15',
    text: 'text-lime-400',
    dotHex: '#84cc16',
    bgHex: 'rgba(132, 204, 22, 0.15)',
    textHex: '#a3e635',
  },
  unibet_eu: {
    id: 'fanduel',
    name: 'Unibet',
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-400',
    dotHex: '#10b981',
    bgHex: 'rgba(16, 185, 129, 0.15)',
    textHex: '#34d399',
  },
  williamhill: {
    id: 'fanduel',
    name: 'William Hill',
    dot: 'bg-violet-500',
    bg: 'bg-violet-500/15',
    text: 'text-violet-400',
    dotHex: '#8b5cf6',
    bgHex: 'rgba(139, 92, 246, 0.15)',
    textHex: '#a78bfa',
  },
  sport888: {
    id: 'fanduel',
    name: '888sport',
    dot: 'bg-fuchsia-500',
    bg: 'bg-fuchsia-500/15',
    text: 'text-fuchsia-400',
    dotHex: '#d946ef',
    bgHex: 'rgba(217, 70, 239, 0.15)',
    textHex: '#e879f9',
  },
};

/** Display name for an Odds API book key (williamhill_us -> Caesars), else the raw key. */
export function getBookDisplayName(oddsApiKey: string): string {
  // Sharp reference books in the odds comparison that are not bettable registry entries.
  const referenceBookNames: Record<string, string> = {
    prophetx: 'ProphetX',
    novig: 'Novig',
    betonlineag: 'BetOnline',
  };
  const id = getSportsbookIdByOddsApiKey(oddsApiKey) ?? oddsApiKey;
  return SPORTSBOOK_COLORS[id]?.name ?? referenceBookNames[oddsApiKey] ?? oddsApiKey;
}
